<template>
    <base-dialog :loading="loading" :value="dialog"
        :title="editMode ? $t('questionnaires.edit_questionnaire') : $t('questionnaires.assign_questionnaire')"
        :width="500" @input="onDialogInput" @close="hide">
        <div v-if="fromPreselected" class="aq-name">{{ questionnaire ? questionnaire.name : '' }}</div>
        <kimsa-combobox v-else :label="$t('common.name')" dense :items="questionnaireOptions"
            :value="questionnaire" @change="questionnaire = $event" ref="questionnaires" no-create-exercise
            :loading="loadingSearch" @search="search = $event" item-value="objectID" />

        <div class="aq-notif-label">{{ $t('common.notification') }}</div>
        <div class="aq-notif-row">
            <kimsa-toggle v-model="hasNotification" />
            <kimsa-combobox v-if="hasNotification" class="aq-hour" :items="hourOptions" :value="hour"
                @change="hour = $event" no-object dense :placeholder="$t('common.hour_notification')" />
        </div>

        <template v-slot:footer>
            <v-btn block depressed x-large color="primary" class="black--text no-radius" :disabled="disabled"
                @click="assign" :loading="loading">
                {{ editMode ? $t('common.edit') : $t('common.assign') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>
import { db, FieldValue, getAllByIds } from "@/utils/Firebase";

import { QuestionnaireIndex } from "@/utils/Algolia";
import { debounce } from '@/utils/Common'
import KimsaCombobox from '../../common/inputs/KimsaCombobox.vue';
import KimsaToggle from '../../common/inputs/KimsaToggle.vue';
import BaseDialog from '../../common/BaseDialog.vue';
import { createNotification, deleteNotification, updateNotification } from "../../../utils/Notifications";

export default {
    name: "AssignQuestionnaireDialog",
    components: { KimsaCombobox, KimsaToggle, BaseDialog },
    props: {
        collection: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            questionnaireOptions: [],
            loadingSearch: false,
            search: '',
            event: null,
            showMenuHour: false,
            questionnaire: null,
            hasNotification: false,
            hour: null,
            fromPreselected: false,
        }
    },
    computed: {
        disabled(vm = this) {
            return !vm.questionnaire || (vm.hasNotification && !vm.hour)
        },
        questionnaireId(vm = this) {
            return vm.questionnaire?.objectID || null
        },
        assignedRoutineId(vm = this) {
            return vm.event?.assignedRoutineId || null
        },
        notificationId(vm = this) {
            return vm.event?.notificationId || null
        },
        isGroup(vm = this) {
            return vm.collection === 'groupAssignedRoutines'
        },
        editMode(vm = this) {
            return vm.event?.assignedQuestionnaireId || null
        },
        hourOptions() {
            return Array.from({ length: 24 }, (_, i) => {
                const h = `${String(i).padStart(2, '0')}:00`
                return { name: h, value: h }
            })
        }
    },
    methods: {
        show(event = null, preselected = null) {
            const vm = this
            vm.fromPreselected = !!preselected
            vm.setDefaults(event, preselected)
            vm.dialog = true
        },
        onDialogInput(val) {
            if (!val) this.hide()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.questionnaireOptions = []
            vm.search = ''
            vm.questionnaire = null
            vm.event = null
            vm.fromPreselected = false
        },
        async setDefaults(event, preselected = null) {
            const vm = this
            await vm.loadQuestionnaireOptions()
            vm.event = event
            vm.hasNotification = !!(event?.notificationId || event?.notificationHour)
            vm.hour = vm.hasNotification ? event?.notificationHour : null
            if (preselected) {
                vm.questionnaire = vm.questionnaireOptions.find(q => q.objectID === preselected.objectID) || preselected
            } else {
                vm.questionnaire = event?.assignedQuestionnaireId ? vm.questionnaireOptions.find(q => q.objectID === event.assignedQuestionnaireId) : null
            }
        },
        debouncedLoadQuestionnaireOptions: debounce(
            function () {
                this.loadQuestionnaireOptions()
            }, 1000),
        async loadQuestionnaireOptions() {
            const vm = this;
            await vm.withLoading(async () => {
                let filter = `coachId: ${vm.coachId}`
                let response = await QuestionnaireIndex.search(vm.search, {
                    filters: filter,
                    hitsPerPage: 100,
                })
                vm.questionnaireOptions = response.hits
            }, 'loadingSearch')
        },
        onHourSelected(hour) {
            this.hour = `${hour.toString().padStart(2, '0')}:00`
            this.$refs.menu.save(this.hour)
        },
        async assign() {
            const vm = this
            await vm.withLoading(async () => {
                const isSameQuestionnaire = vm.editMode === vm.questionnaireId

                const saved = vm.isGroup ? await vm.assignToGroup() : await vm.assignToStudent()
                if (!saved) return vm.hide()

                if (!isSameQuestionnaire) {
                    await db.collection('questionnaires').doc(vm.questionnaireId).update({ used: FieldValue.increment(1) })
                }

                vm.toast(vm.$t('questionnaires.assigned_questionnaire'))
                vm.$emit('assigned', { assignedRoutineId: vm.assignedRoutineId, questionnaireId: vm.questionnaireId })
                vm.hide()
            })
        },
        async assignToStudent() {
            const vm = this
            const notification = await vm.manageNotification(vm.assignedRoutineId, {
                studentId: vm.event?.studentId || null,
                notificationId: vm.notificationId,
                notificationHour: vm.event?.notificationHour || null,
            })
            const saved = await vm.saveAssignedRoutine(vm.assignedRoutineId, {
                assignedQuestionnaireId: vm.questionnaireId,
                ...notification,
            }, vm.collection)
            if (!saved && !vm.notificationId && notification.notificationId) {
                await deleteNotification(notification.notificationId).catch(() => { })
            }
            return saved
        },
        async assignToGroup() {
            const vm = this
            const groupRef = await db.collection(vm.collection).doc(vm.assignedRoutineId).get()
            const assignedRoutinesIds = groupRef.data()?.assignedRoutinesIds || []

            if (vm.notificationId) await deleteNotification(vm.notificationId).catch(() => { })

            const saved = await vm.saveAssignedRoutine(vm.assignedRoutineId, {
                assignedQuestionnaireId: vm.questionnaireId,
                notificationId: null,
                notificationHour: vm.hasNotification ? vm.hour : null,
            }, vm.collection)
            if (!saved) return false

            const routines = await getAllByIds('assignedRoutines', assignedRoutinesIds)
            await Promise.all(routines.map(async routine => {
                const notification = await vm.manageNotification(routine.id, {
                    studentId: routine.studentId || null,
                    notificationId: routine.notificationId || null,
                    notificationHour: routine.notificationHour || null,
                })
                await db.collection('assignedRoutines').doc(routine.id).update({
                    assignedQuestionnaireId: vm.questionnaireId,
                    ...notification,
                }).catch(err => console.log('err assignedQuestionnaireId update', err))
            }))

            return true
        },
        async manageNotification(assignedRoutineId, { studentId = null, notificationId = null, notificationHour = null } = {}) {
            const vm = this;
            if (!vm.hasNotification) {
                if (notificationId) await deleteNotification(notificationId).catch(() => { })
                return { notificationId: null, notificationHour: null }
            }

            const payloadNotification = {
                hour: vm.hour,
                message: vm.$t('questionnaires.new_questionnaire_available'),
                path: `/student/routines/${assignedRoutineId}`,
                date: vm.event.date,
                userId: studentId,
                assignedRoutineId: assignedRoutineId,
            }

            if (notificationId) {
                if (notificationHour !== vm.hour) await updateNotification(notificationId, payloadNotification)
                return { notificationId: notificationId, notificationHour: vm.hour }
            }

            const notificationRef = await createNotification(payloadNotification)
            return { notificationId: notificationRef.id, notificationHour: vm.hour }
        }
    },
    watch: {

        search() {
            this.debouncedLoadQuestionnaireOptions()
        },
    }
}
</script>

<style scoped>
.aq-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    padding: 4px 0;
}

.aq-notif-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-top: 16px;
}

.aq-notif-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.aq-hour {
    flex: 1;
}
</style>